import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

export const KyuStoreAntiMinusOpsi3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Palette: Clean Esport Komersial (Light Mode Theme)
  const bgCanvas = "#F8F6FE";
  const bgCard = "#FFFFFF";
  const kyuPurple = "#5D3FD3";
  const kyuDark = "#120A2E";
  const kyuLavender = "#F3E8FF";
  const kyuBorder = "#E9D5FF";
  const accentGold = "#FFB020";
  const textDark = "#0F172A";
  const textMuted = "#64748B";
  const emeraldSuccess = "#10B981";
  const emeraldLight = "#ECFDF5";
  const coralAlert = "#EF4444";
  const coralLight = "#FEF2F2";
  const coralBorder = "#FECACA";

  // Easing standard
  const EASE = Easing.bezier(0.16, 1, 0.3, 1);

  // Springs helper
  const sp = (delay: number, damping = 14, stiffness = 125) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Ambient micro-motion breathing float
  const floatY = Math.sin(frame / 22) * 4.5;

  // ==========================================
  // DYNAMIC SUBTITLES MAPPING (Strictly at bottom: 220px)
  // Total: 1220 frames (~40.67s @ 30fps)
  // ==========================================
  const SUBTITLES = [
    { from: 0, to: 150, text: "Jangan sampai akun Mobile Legends yang udah kamu bangun capek-capek sampai Mythic...", highlight: "sampai Mythic" },
    { from: 150, to: 295, text: "...malah kena ban atau diamond-nya minus gara-gara top up sembarangan!", highlight: "diamond-nya minus" },
    { from: 295, to: 440, text: "Banyak penjual nakal nawarin harga murah gak masuk akal, tapi minta login password...", highlight: "minta login password" },
    { from: 440, to: 585, text: "...atau pakai sistem refund ilegal yang bikin akunmu kena sanksi Moonton.", highlight: "sistem refund ilegal" },
    { from: 585, to: 740, text: "Di Kyu Store, semua transaksi 100% resmi, legal, dan bergaransi anti-minus.", highlight: "100% resmi, legal, dan bergaransi" },
    { from: 740, to: 920, text: "Kami gak pernah minta password akun kamu—cukup User ID dan Zone ID saja.", highlight: "cukup User ID dan Zone ID" },
    { from: 920, to: 1060, text: "Bayar instan pakai QRIS, diamond langsung meluncur masuk detik itu juga.", highlight: "langsung meluncur masuk" },
    { from: 1060, to: 1220, text: "Jaga akunmu tetap aman. Top up resmi hanya di kyustore.com!", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // ==========================================
  // TIMELINE SCENE CHOREOGRAPHY
  // Scene 1 (The Warning): Frame 0 - 295
  // Scene 2 (The Trap Exposed): Frame 290 - 585
  // Scene 3 (Kyu Store Safe Haven): Frame 580 - 920
  // Scene 4 (Instant QRIS & CTA): Frame 915 - 1220
  // ==========================================

  // --- Corner HUD Anchors ---
  const hudSpring = sp(0, 16, 120);
  const hudOpacity = interpolate(hudSpring, [0, 1], [0, 1]);
  const hudY = interpolate(hudSpring, [0, 1], [-20, 0]);

  // --- Scene 1: The Warning (Mythic Alert) ---
  const s1In = sp(8, 13, 115);
  const s1Y = interpolate(s1In, [0, 1], [60, 0]);
  const s1Scale = interpolate(s1In, [0, 1], [0.92, 1]);
  const s1Opacity = interpolate(s1In, [0, 1], [0, 1]);
  const s1Glitch = frame >= 120 && frame <= 145 ? Math.sin(frame * 2.5) * 3 : 0;

  const s1Exit = interpolate(frame, [280, 298], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s1ExitY = interpolate(s1Exit, [0, 1], [0, -60]);
  const s1ExitOpacity = interpolate(s1Exit, [0, 0.8], [1, 0]);

  // --- Scene 2: The Trap Exposed (Top Up Ilegal) ---
  const s2In = sp(292, 14, 125);
  const s2Y = interpolate(s2In, [0, 1], [60, 0]);
  const s2Scale = interpolate(s2In, [0, 1], [0.9, 1]);
  const s2Opacity = interpolate(s2In, [0, 1], [0, 1]);

  const s2CrossBounce = sp(340, 12, 160);
  const s2CrossScale = interpolate(s2CrossBounce, [0, 1], [0, 1.15]);

  const s2Exit = interpolate(frame, [570, 588], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s2ExitY = interpolate(s2Exit, [0, 1], [0, -60]);
  const s2ExitOpacity = interpolate(s2Exit, [0, 0.8], [1, 0]);

  // --- Scene 3: Kyu Store Safe Haven ---
  const s3In = sp(582, 13, 120);
  const s3Y = interpolate(s3In, [0, 1], [70, 0]);
  const s3Scale = interpolate(s3In, [0, 1], [0.9, 1]);
  const s3Opacity = interpolate(s3In, [0, 1], [0, 1]);

  // Specular sweep across Kyu Store Card
  const sweepProgress = interpolate(frame, [630, 680], [-100, 200], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: EASE,
  });

  const s3Exit = interpolate(frame, [905, 922], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const s3ExitY = interpolate(s3Exit, [0, 1], [0, -60]);
  const s3ExitOpacity = interpolate(s3Exit, [0, 0.8], [1, 0]);

  // --- Scene 4: Instant QRIS & Payoff CTA ---
  const s4In = sp(916, 13, 125);
  const s4Y = interpolate(s4In, [0, 1], [70, 0]);
  const s4Scale = interpolate(s4In, [0, 1], [0.9, 1]);
  const s4Opacity = interpolate(s4In, [0, 1], [0, 1]);

  // QRIS laser scan loop
  const scanLaserY = Math.sin(frame / 6) * 55;

  // CTA pulse & click
  const ctaSpring = sp(980, 14, 130);
  const ctaScaleIn = interpolate(ctaSpring, [0, 1], [0.85, 1]);
  const isClicking = frame >= 1115 && frame <= 1130;
  const ctaClickScale = isClicking ? 0.94 : 1.0;

  // Cursor animation
  const cursorProgress = interpolate(frame, [1085, 1115], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: EASE,
  });
  const cursorX = interpolate(cursorProgress, [0, 1], [720, 520]);
  const cursorY = interpolate(cursorProgress, [0, 1], [520, 360]);
  const cursorOpacity = interpolate(frame, [1085, 1095], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCanvas,
        fontFamily: "'Rajdhani', -apple-system, sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* 1. Global Audio Elements */}
      <Audio src={staticFile("kyustore_antiminus_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.12} />

      {/* Synchronized Micro-SFX */}
      {frame === 8 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.65} />}
      {frame === 292 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.65} />}
      {frame === 582 && <Audio src={staticFile("whoosh.mp3")} volume={0.65} />}
      {frame === 610 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 916 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 960 && <Audio src={staticFile("ui-success.mp3")} volume={0.75} />}
      {frame === 1118 && <Audio src={staticFile("click.mp3")} volume={0.8} />}

      {/* 2. Background Halftone & Dot Grid (Light Mode) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* 3. Multi-Layer Optical Violet Glow in Center */}
      <div
        style={{
          position: "absolute",
          top: "46%",
          left: "50%",
          width: 820,
          height: 820,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.12) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(75px)",
          pointerEvents: "none",
        }}
      />

      {/* ========================================================================= */}
      {/* CORNER HUD ANCHORS (Esports Credibility Framing)                           */}
      {/* ========================================================================= */}
      {/* Top-Left: Logo & Category */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 90,
          display: "flex",
          alignItems: "center",
          gap: 12,
          transform: `translateY(${hudY}px)`,
          opacity: hudOpacity,
          zIndex: 40,
        }}
      >
        <Img src={staticFile("kyu_logo.png")} style={{ height: 34, objectFit: "contain" }} />
        <div style={{ width: 1.5, height: 22, backgroundColor: "#CBD5E1" }} />
        <span
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: textMuted,
          }}
        >
          [ OFFICIAL MOONTON PARTNER ]
        </span>
      </div>

      {/* Top-Right: 24/7 Security Status */}
      <div
        style={{
          position: "absolute",
          top: 80,
          right: 90,
          display: "flex",
          alignItems: "center",
          gap: 8,
          backgroundColor: bgCard,
          border: `1.5px solid ${emeraldSuccess}`,
          padding: "6px 18px",
          borderRadius: 20,
          boxShadow: "0 4px 15px rgba(16, 185, 129, 0.12)",
          transform: `translateY(${hudY}px)`,
          opacity: hudOpacity,
          zIndex: 40,
        }}
      >
        <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: emeraldSuccess, boxShadow: "0 0 8px #10B981" }} />
        <span
          style={{
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            color: emeraldSuccess,
            letterSpacing: "0.08em",
          }}
        >
          100% LEGAL & AMAN
        </span>
      </div>

      {/* ========================================================================= */}
      {/* MASTER STAGE: DEAD-CENTERED (top: 48%, left: 50%, translate -50%, -50%)   */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          top: "48%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* ================================================================= */}
        {/* SCENE 1: THE WAKE-UP CALL / WARNING (Frame 0 - 295)               */}
        {/* ================================================================= */}
        {frame < 298 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${s1Y + s1ExitY + floatY}px) scale(${s1Scale})`,
              opacity: s1Opacity * s1ExitOpacity,
            }}
          >
            {/* Warning Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: coralLight,
                border: `2px solid ${coralBorder}`,
                padding: "8px 24px",
                borderRadius: 24,
                marginBottom: 16,
                boxShadow: "0 4px 15px rgba(239, 68, 68, 0.12)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="2.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span
                style={{
                  fontFamily: "'Chakra Petch', sans-serif",
                  fontSize: 16,
                  fontWeight: 700,
                  color: coralAlert,
                  letterSpacing: "0.08em",
                }}
              >
                PERINGATAN AKUN MOBILE LEGENDS
              </span>
            </div>

            {/* Monumental Headline in Bebas Neue */}
            <h1
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 66,
                fontWeight: 400,
                lineHeight: 1.05,
                textAlign: "center",
                letterSpacing: "0.02em",
                marginBottom: 24,
              }}
            >
              AKUN MYTHIC KENA BANNED? <br />
              <span style={{ color: coralAlert }}>JANGAN ASAL PILIH TEMPAT TOP UP!</span>
            </h1>

            {/* Warning Elevated Card */}
            <div
              style={{
                width: 860,
                backgroundColor: bgCard,
                borderRadius: 36,
                border: `2.5px solid ${coralBorder}`,
                padding: "36px 40px",
                boxShadow: "0 25px 65px rgba(239, 68, 68, 0.14), 0 8px 20px rgba(0,0,0,0.04)",
                position: "relative",
                overflow: "hidden",
                transform: `translateX(${s1Glitch}px)`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <Img src={staticFile("game_mlbb.jpg")} style={{ width: 64, height: 64, borderRadius: 18, border: "2px solid #E2E8F0" }} />
                  <div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, letterSpacing: "0.02em" }}>
                      MOBILE LEGENDS: BANG BANG
                    </div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, fontWeight: 700, color: textMuted }}>
                      Akun Rank: <span style={{ color: accentGold, fontWeight: 900 }}>Mythical Glory 84 Bintang</span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: coralLight,
                    border: `1.5px solid ${coralAlert}`,
                    color: coralAlert,
                    fontFamily: "'Chakra Petch', sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    padding: "8px 18px",
                    borderRadius: 16,
                  }}
                >
                  STATUS: TERANCAM SANKSI
                </div>
              </div>

              {/* Minus Diamond Alert Display */}
              <div
                style={{
                  marginTop: 26,
                  backgroundColor: "#FFF5F5",
                  border: `2px dashed ${coralAlert}`,
                  borderRadius: 24,
                  padding: "24px 30px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: textMuted }}>
                    SALDO DIAMOND SAAT INI:
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                    <span
                      style={{
                        fontFamily: "'Anton', sans-serif",
                        fontSize: 52,
                        color: coralAlert,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      -1.500
                    </span>
                    {/* Pure Vector Diamond Icon */}
                    <svg width="34" height="34" viewBox="0 0 24 24" fill={coralAlert} stroke="#FFFFFF" strokeWidth="1.5">
                      <path d="M6 3h12l4 6-10 12L2 9Z" />
                      <path d="M11 3 8 9l4 12 4-12-3-6" fill="#FCA5A5" />
                      <path d="M2 9h20" stroke="#FFFFFF" strokeWidth="1.2" />
                    </svg>
                    <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 34, color: coralAlert, letterSpacing: "0.04em" }}>
                      (MINUS)
                    </span>
                  </div>
                </div>

                {/* Stempel BANNED */}
                <div
                  style={{
                    border: `4px solid ${coralAlert}`,
                    color: coralAlert,
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 34,
                    letterSpacing: "0.1em",
                    padding: "8px 24px",
                    borderRadius: 16,
                    transform: "rotate(-8deg)",
                    boxShadow: "0 4px 15px rgba(239, 68, 68, 0.2)",
                  }}
                >
                  SANKSI MOONTON
                </div>
              </div>

              <div
                style={{
                  marginTop: 20,
                  fontSize: 16,
                  color: textMuted,
                  fontWeight: 700,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>Akun tidak bisa masuk ranked & terancam suspend permanen jika diamond minus tidak dibayar.</span>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* SCENE 2: THE TRAP EXPOSED (Frame 290 - 585)                       */}
        {/* ================================================================= */}
        {frame >= 288 && frame < 588 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${s2Y + s2ExitY + floatY}px) scale(${s2Scale})`,
              opacity: s2Opacity * s2ExitOpacity,
            }}
          >
            {/* Header Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: coralLight,
                border: `2px solid ${coralBorder}`,
                padding: "8px 24px",
                borderRadius: 24,
                marginBottom: 16,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
              </svg>
              <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, color: coralAlert, letterSpacing: "0.08em" }}>
                MODUS SELLER ILEGAL & CALO NAKAL
              </span>
            </div>

            {/* Headline */}
            <h1
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 66,
                fontWeight: 400,
                lineHeight: 1.05,
                textAlign: "center",
                letterSpacing: "0.02em",
                marginBottom: 24,
              }}
            >
              HARGA MURAH GAK MASUK AKAL, <br />
              <span style={{ color: coralAlert }}>TAPI MINTA PASSWORD AKUN GAME KAMU!</span>
            </h1>

            {/* Danger Card Grid */}
            <div
              style={{
                width: 860,
                backgroundColor: bgCard,
                borderRadius: 36,
                border: `2.5px solid ${coralBorder}`,
                padding: "36px",
                boxShadow: "0 25px 65px rgba(239, 68, 68, 0.12)",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                {/* Form Input Trap Mockup */}
                <div style={{ flex: 1, backgroundColor: "#FFF8F8", border: "1.5px solid #FEE2E2", borderRadius: 24, padding: "24px" }}>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, fontWeight: 700, color: coralAlert, marginBottom: 12 }}>
                    CONTOH FORM TOP UP JEBAKAN:
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ backgroundColor: "#FFFFFF", border: "1.5px solid #E2E8F0", padding: "12px 16px", borderRadius: 14 }}>
                      <span style={{ fontSize: 13, color: textMuted }}>Email / Akun Moonton:</span>
                      <div style={{ fontSize: 16, fontWeight: 700, color: textDark }}>player_mlbb@gmail.com</div>
                    </div>

                    <div style={{ backgroundColor: "#FFFFFF", border: `2px solid ${coralAlert}`, padding: "12px 16px", borderRadius: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="2.5">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                          <line x1="12" y1="9" x2="12" y2="13"></line>
                          <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        <span style={{ fontSize: 13, color: coralAlert, fontWeight: 700 }}>Password Akun (BAHAYA):</span>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: coralAlert }}>••••••••••••••</div>
                    </div>
                  </div>
                </div>

                {/* Massive Red Cross Stamp */}
                <div style={{ width: 260, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div
                    style={{
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      backgroundColor: coralLight,
                      border: `3px solid ${coralAlert}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transform: `scale(${s2CrossScale})`,
                      boxShadow: "0 10px 30px rgba(239, 68, 68, 0.25)",
                    }}
                  >
                    <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3.5">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </div>

                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, color: coralAlert, marginTop: 14, textAlign: "center" }}>
                    JANGAN PERNAH KASIH PASSWORD!
                  </div>
                </div>
              </div>

              {/* Warning Checklist with Pure SVGs */}
              <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", borderTop: "1.5px solid #FEE2E2", paddingTop: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: coralAlert }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span>Rawan Phishing & Hack Akun</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: coralAlert }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span>Modus Refund Ilegal</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: coralAlert }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={coralAlert} strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span>Akun Terancam Suspend</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* SCENE 3: KYU STORE SAFE HAVEN (Frame 580 - 920)                   */}
        {/* ================================================================= */}
        {frame >= 578 && frame < 924 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${s3Y + s3ExitY + floatY}px) scale(${s3Scale})`,
              opacity: s3Opacity * s3ExitOpacity,
            }}
          >
            {/* Header Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: emeraldLight,
                border: `2px solid ${emeraldSuccess}`,
                padding: "8px 24px",
                borderRadius: 24,
                marginBottom: 16,
                boxShadow: "0 4px 15px rgba(16, 185, 129, 0.12)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
              </svg>
              <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.08em" }}>
                SOLUSI RESMI: KYU STORE INDONESIA
              </span>
            </div>

            {/* Headline */}
            <h1
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 66,
                fontWeight: 400,
                lineHeight: 1.05,
                textAlign: "center",
                letterSpacing: "0.02em",
                marginBottom: 24,
              }}
            >
              100% RESMI & ANTI-MINUS, <br />
              <span style={{ color: kyuPurple }}>CUKUP USER ID & ZONE ID SAJA!</span>
            </h1>

            {/* Elevated Clean Esport Card */}
            <div
              style={{
                width: 860,
                backgroundColor: bgCard,
                borderRadius: 36,
                border: `2.5px solid ${kyuPurple}`,
                padding: "36px 40px",
                boxShadow: "0 30px 80px rgba(93, 63, 211, 0.18), 0 10px 25px rgba(0,0,0,0.04)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Specular Light Sweep */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: 130,
                  left: `${sweepProgress}%`,
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
                  transform: "skewX(-25deg)",
                  pointerEvents: "none",
                }}
              />

              <div style={{ display: "flex", gap: 26, alignItems: "center" }}>
                {/* 3D Prismatic Diamond Artwork Staging */}
                <div
                  style={{
                    width: 210,
                    height: 200,
                    borderRadius: 28,
                    backgroundColor: kyuLavender,
                    border: "2px solid #D8B4FE",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "inset 0 2px 10px rgba(255, 255, 255, 0.8), 0 12px 30px rgba(93, 63, 211, 0.15)",
                  }}
                >
                  {/* Prismatic 3D Diamond Vector */}
                  <svg width="90" height="90" viewBox="0 0 100 100" style={{ filter: "drop-shadow(0 10px 25px rgba(93, 63, 211, 0.45))" }}>
                    <defs>
                      <linearGradient id="facetTop" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFFFFF" />
                        <stop offset="100%" stopColor="#BAE6FD" />
                      </linearGradient>
                      <linearGradient id="facetCyan" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#38BDF8" />
                        <stop offset="100%" stopColor="#0284C7" />
                      </linearGradient>
                      <linearGradient id="facetPurple" x1="100%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#5B21B6" />
                      </linearGradient>
                    </defs>
                    <polygon points="26,16 74,16 88,38 12,38" fill="url(#facetTop)" stroke="#FFFFFF" strokeWidth="0.8" />
                    <polygon points="12,38 50,38 50,92" fill="url(#facetCyan)" stroke="#38BDF8" strokeWidth="0.8" />
                    <polygon points="50,38 88,38 50,92" fill="url(#facetPurple)" stroke="#6366F1" strokeWidth="0.8" />
                    <polygon points="26,16 50,38 74,16" fill="#F0F9FF" stroke="#FFFFFF" strokeWidth="0.8" />
                    <circle cx="50" cy="38" r="5" fill="#FFFFFF" opacity="0.9" />
                  </svg>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={kyuPurple} stroke="#FFFFFF" strokeWidth="1.2">
                      <path d="M6 3h12l4 6-10 12L2 9Z" />
                    </svg>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 900, color: kyuPurple }}>
                      TOP UP RESMI
                    </span>
                  </div>
                </div>

                {/* Safe Form Demonstration */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 14 }}>
                    <div style={{ flex: 1.4, backgroundColor: "#F8FAFC", border: "1.5px solid #CBD5E1", padding: "14px 18px", borderRadius: 16 }}>
                      <span style={{ fontSize: 13, color: textMuted, fontWeight: 700 }}>User ID:</span>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 24, fontWeight: 900, color: textDark }}>84920194</div>
                    </div>

                    <div style={{ flex: 1, backgroundColor: "#F8FAFC", border: "1.5px solid #CBD5E1", padding: "14px 18px", borderRadius: 16 }}>
                      <span style={{ fontSize: 13, color: textMuted, fontWeight: 700 }}>Zone ID:</span>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 24, fontWeight: 900, color: textDark }}>(2041)</div>
                    </div>
                  </div>

                  {/* High Trust Guarantee Banner */}
                  <div
                    style={{
                      marginTop: 16,
                      backgroundColor: emeraldLight,
                      border: `1.5px solid ${emeraldSuccess}`,
                      borderRadius: 18,
                      padding: "14px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 900, color: emeraldSuccess }}>
                      100% AMAN — TANPA PASSWORD SAMA SEKALI!
                    </span>
                  </div>

                  <div style={{ marginTop: 12, display: "flex", gap: 18, fontSize: 14, fontWeight: 800, color: textMuted }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Anti-Minus Akun Garansi</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Realtime Moonton API</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>24 Jam Nonstop</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* SCENE 4: INSTANT QRIS & CTA PAYOFF (Frame 915 - 1220)              */}
        {/* ================================================================= */}
        {frame >= 912 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${s4Y + floatY}px) scale(${s4Scale})`,
              opacity: s4Opacity,
            }}
          >
            {/* Header Badge */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <Img src={staticFile("satset_proses.png")} style={{ height: 46, objectFit: "contain" }} />
            </div>

            {/* Headline */}
            <h1
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 66,
                fontWeight: 400,
                lineHeight: 1.05,
                textAlign: "center",
                letterSpacing: "0.02em",
                marginBottom: 24,
              }}
            >
              SCAN QRIS SATSET 1 DETIK, <br />
              <span style={{ color: kyuPurple }}>DIAMOND LANGSUNG AKTIF MASUK AKUN!</span>
            </h1>

            {/* Split Screen Card (QRIS Left vs Success Right) */}
            <div style={{ width: 860, display: "flex", gap: 24, alignItems: "center" }}>
              {/* Left Box: QRIS Scanner */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: bgCard,
                  borderRadius: 30,
                  border: `2px solid ${kyuBorder}`,
                  padding: "26px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxShadow: "0 20px 45px rgba(93, 63, 211, 0.12)",
                }}
              >
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, fontWeight: 900, color: kyuDark, marginBottom: 12 }}>
                  QRIS SEMUA BANK & E-WALLET
                </div>

                {/* QR Code Container with Laser Beam */}
                <div
                  style={{
                    width: 200,
                    height: 200,
                    backgroundColor: "#111827",
                    borderRadius: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <svg width="125" height="125" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                    <rect x="2" y="2" width="8" height="8" rx="1"></rect>
                    <rect x="14" y="2" width="8" height="8" rx="1"></rect>
                    <rect x="14" y="14" width="8" height="8" rx="1"></rect>
                    <rect x="2" y="14" width="8" height="8" rx="1"></rect>
                    <line x1="6" y1="6" x2="6" y2="6"></line>
                    <line x1="18" y1="6" x2="18" y2="6"></line>
                    <line x1="18" y1="18" x2="18" y2="18"></line>
                    <line x1="6" y1="18" x2="6" y2="18"></line>
                  </svg>

                  {/* Cyan Laser Beam */}
                  <div
                    style={{
                      position: "absolute",
                      left: 10,
                      right: 10,
                      top: 100 + scanLaserY,
                      height: 4,
                      backgroundColor: "#00F0FF",
                      boxShadow: "0 0 14px #00F0FF, 0 0 24px #00F0FF",
                    }}
                  />
                </div>
              </div>

              {/* Right Box: Verified Injection Status */}
              <div style={{ flex: 1.15, display: "flex", flexDirection: "column", gap: 16 }}>
                <div
                  style={{
                    backgroundColor: bgCard,
                    borderRadius: 26,
                    border: `2px solid ${emeraldSuccess}`,
                    padding: "24px",
                    boxShadow: "0 18px 40px rgba(16, 185, 129, 0.16)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, color: emeraldSuccess, display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      TRANSAKSI BERHASIL
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={accentGold} strokeWidth="2.5">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                      <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#64748B", fontWeight: 800 }}>0.8 DETIK</span>
                    </div>
                  </div>

                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, letterSpacing: "0.02em", marginTop: 8 }}>
                    Weekly Diamond Pass Aktif
                  </div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: textMuted, marginTop: 4 }}>
                    User ID: <span style={{ fontWeight: 800, color: textDark }}>84920194 (2041)</span> • Otomatis masuk
                  </div>
                </div>

                {/* High Converting CTA Button */}
                <div
                  style={{
                    backgroundColor: kyuDark,
                    color: "#FFFFFF",
                    border: `2px solid ${accentGold}`,
                    borderRadius: 28,
                    padding: "18px 30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "0 18px 45px rgba(18, 10, 46, 0.3)",
                    transform: `scale(${ctaScaleIn * ctaClickScale})`,
                  }}
                >
                  <div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: "0.03em" }}>
                      TOP UP RESMI SEKARANG
                    </div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: accentGold, fontWeight: 700 }}>
                      Kunjungi website: kyustore.com
                    </div>
                  </div>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={accentGold} strokeWidth="3">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>
              </div>
            </div>

            {/* Mouse Pointer Clicking CTA */}
            <div
              style={{
                position: "absolute",
                left: cursorX,
                top: cursorY,
                zIndex: 35,
                display: "flex",
                alignItems: "center",
                gap: 8,
                filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.3))",
                opacity: cursorOpacity,
                transform: `scale(${isClicking ? 0.88 : 1.0})`,
              }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="#120A2E" stroke="#FFFFFF" strokeWidth="2">
                <path d="M3 3l7 18 3-7 7-3L3 3z"/>
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* CORNER FOOTER TICKER (Bank & E-Wallet Whitelist)                          */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 90,
          right: 90,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: bgCard,
          border: "1.5px solid #E2E8F0",
          borderRadius: 20,
          padding: "12px 28px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 35,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 800, color: textMuted }}>
          <span style={{ color: kyuPurple }}>METODE RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'Chakra Petch', sans-serif", fontSize: 13, fontWeight: 700, color: emeraldSuccess }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>MOONTON DIRECT INJECT</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: RAISED TO OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 400, // Raised to optimal reading height (Y: 1520px, clear of platform UI)
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(18, 10, 46, 0.94)",
              backdropFilter: "blur(14px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 840,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 26, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.35 }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: accentGold }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
